import update from 'immutability-helper';

/**
 * Iterate through all dict items (in no particular order).
 *
 * Usage:
 *
 *   const dict = {one: 'This is one', two: 'This is two'};
 *
 *   forEachDict(dict, (key, value) => {
 *     console.log(key, ' -> ', value);
 *   });
 *
 * Output:
 *   one -> This is one
 *   two -> This is two
 *
 */
export function forEachDict(dict: any, fn: (k: string, v: any) => void): void {
  Object.keys(dict).forEach((key) => {
    fn(key, dict[key]);
  });
}

/**
 * Returns a new dict, replacing all items by the result of passing
 * each item through the function.
 *
 * Usage:
 *
 *   const dict = {one: 1, two: 2};
 *
 *   console.log(mapDict(dict, (key, value) => value * 2)));
 *
 * Output:
 *
 *   {one: 2, two: 4}
 *
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function mapDict(dict: any, fn: (k: string, v: any) => {}): {} {
  let newDict = dict;
  Object.keys(dict).forEach((key) => {
    newDict = update(newDict, {
      [key]: { $set: fn(key, dict[key]) }
    });
  });
  return newDict;
}

export function filterDict(dict: any, fn: (k: string, t: any) => boolean): Record<string, any> {
  return Object.keys(dict).reduce((filtered: any, key) => {
    if (fn(key, dict[key])) {
      filtered[key] = dict[key];
    }

    return filtered;
  }, {});
}

/**
 * Iterates through a tree made by objects and arrays until it
 * reaches a leaf. In that point it executes the callback for each leaf
 */
export function eachLeaf(dict: any, fn: (k: string, t: any) => void) {
  if (dict) {
    Object.keys(dict).forEach((key) => {
      if (dict[key] instanceof Object) {
        eachLeaf(dict[key], fn);
      } else {
        fn(key, dict[key]);
      }
    });
  }
}

/**
 * Returns an immutable-helper query with when the predicate matches
 * and with root for valueFn
 *
 * updateQuery({ "a": { "b": { "c": 10 }}}, (k, v) => k === "c", () => { $set: 20 })
 *
 * Returns:
 *
 * { "a": { "b": { "c": { $set: 20 }}}}
 */
export function updateQuery(dict: any, predicate: (k: any, v: any) => boolean, valueFn: (k: any, v: any) => any): any {
  if (dict && dict instanceof Object) {
    for (const key of Object.keys(dict)) {
      // If the condition is true we return the value
      if (predicate(key, dict[key])) {
        return valueFn(key, dict[key]);
      }
      // We compose the update query
      const result = updateQuery(dict[key], predicate, valueFn);
      if (result) {
        return { [key]: { ...result } };
      }
    }
  }
  return undefined;
}

export function zip<T1, T2>(arr1: T1[], arr2: T2[]): [T1 | undefined, T2 | undefined][] {
  const toIdx = Math.max(arr1.length, arr2.length);

  const result: [T1, T2][] = [];
  for (let i = 0; i < toIdx; i++) {
    result.push([arr1[i], arr2[i]]);
  }

  return result;
}

export function join<T1, T2>(arr1: T1[], arr2: T2[], cond?: (t1: T1, t2: T2) => boolean): [T1, T2][] {
  return arr1.reduce(
    (acc, t1) => acc.concat(
      arr2.filter((t2) => !isDefined(cond) || cond(t1, t2)).map((t2) => [t1, t2] as [T1, T2])
    ),
    [] as [T1, T2][]
  );
}

/**
 * flatArray([[1], [2]], [3])
 *
 * Returns:
 * [1, 2, 3]
 */
export function flatArray<T>(array: T[][]): T[] {
  return array.reduce((acc, val) => acc.concat(val), []);
}

/**
 * deleteFromArray([1, 2, 3], 2)
 *
 * Returns:
 * [1, 3]
 */
export function deleteFromArray(array: any[], elementToDelete: any) {
  const index = array.indexOf(elementToDelete);

  if (index > -1) {
    return update(array, {
      $splice: [[index, 1]]
    });
  }
  return array;
}

/**
 * Flattens the nested dicts as one dict with composite keys
 *
 * flattenDictKeys({ 'first': { 'b': {'c': 2}, 'd': 'test'}, 'second': { 'a': 1 }})
 *
 * returns:
 * {
 *  'first.b.c': 2,
 *  'first.test': 'test',
 *  'second.a': 1
 * }
 */
export function flattenDictKeys(obj: Record<string, any>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const k of Object.keys(obj)) {
    const value = obj[k];

    if (value instanceof Object) {
      const flattened = flattenDictKeys(value);

      for (const k2 of Object.keys(flattened)) {
        result[`${k}.${k2}`] = flattened[k2];
      }
    } else {
      result[k] = value;
    }
  }
  return result;
}
